import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Divider,
  Form,
  Grid,
  Layout,
  Menu,
  Select,
  Space,
  Tooltip,
} from "antd";
import QuickbooksContext from "../../context/QuickbooksContext";
import { NoteProps, TagProps, UserProps } from "../../interfaces/interfaces";
import { ActivityList } from "../activity";
import { TaskList } from "../task";
import DataContext from "../../context/DataContext";
import {
  ACTIVITY_INITIAL_STATE,
  CONTACT_INITIAL_STATE,
  NOTE_INITIAL_STATE,
  TASK_INITIAL_STATE,
} from "../../utils/data";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import {
  UserAddOutlined,
  FileAddOutlined,
  FormOutlined,
  ClockCircleOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  ZoomInOutlined,
  FolderAddOutlined,
} from "@ant-design/icons";
import { NoteList } from "./note/NoteList";
import { ContactList } from "./contact/ContactList";
import { ModalCreateOrUpdateNote } from "./note/ModalCreateOrUpdateNote";
import AuthContext from "../../context/AuthContext";
import Sider from "antd/es/layout/Sider";
import { Content, Header } from "antd/es/layout/layout";
import { MenuProps } from "antd/lib";
import { TagSelector } from "../ui";
import { filterOption } from "../../utils/func/utils";
dayjs.extend(duration);

export const CustomerInfo = ({
  idClient,
  onCreateProject,
}: {
  idClient: string;
  onCreateProject: (fromSelected?: boolean) => void;
}) => {
  const { useBreakpoint } = Grid;
  const { xs } = useBreakpoint();
  const [siderWidth, setSiderWidth] = useState<number>(450);
  const [collapsed, setCollapsed] = useState(true);
  const { customerInfo, getCustomerData } = useContext(QuickbooksContext);
  const {
    userOptions,
    contactList,
    projectSelected,
    openItemModal,
    handleEditData,
    getData,
    handleDeleteData,
  } = useContext(DataContext);
  const { profileInfo } = useContext(AuthContext);
  const [projectAssigned, setProjectAssigned] = useState<UserProps[]>([]);
  const [noteModal, setNoteModal] = useState<{
    open: boolean;
    payload: NoteProps | null;
  }>({ open: false, payload: null });
  const [tagsSelected, setTagsSelected] = useState<TagProps[]>([]);

  useEffect(() => {
    getData("activities-paginated", idClient, undefined, {
      project: projectSelected,
      customer: customerInfo._id,
      origin: "customer",
    });
    getData("tasks", idClient, undefined, {
      project: projectSelected,
      customer: customerInfo._id,
      active: true,
    });

    getData("contacts", idClient, undefined, {
      customers: customerInfo._id,
    });
    let assigned = customerInfo.Projects?.find(
      (project) => project?.id?._id === projectSelected,
    )?.assignedTo;
    assigned && setProjectAssigned(assigned as UserProps[]);
    return () => {
      setCollapsed(true);
    };
  }, [projectSelected, customerInfo]);

  const handleFilterByTags = (selectedTags: TagProps[]) => {
    setTagsSelected(selectedTags);
    if (selectedTags.length > 0) {
      getData("activities-paginated", idClient, undefined, {
        project: projectSelected,
        customer: customerInfo._id,
        origin: "customer",
        tags: {
          $in: selectedTags.map((tag) => tag._id),
        },
      });
      getData("tasks", idClient, undefined, {
        project: projectSelected,
        customer: customerInfo._id,
        active: true,
        tags: {
          $in: selectedTags.map((tag) => tag._id),
        },
      });
    } else {
      getData("activities-paginated", idClient, undefined, {
        project: projectSelected,
        customer: customerInfo._id,
        active: true,
      });
      getData("tasks", idClient, undefined, {
        project: projectSelected,
        customer: customerInfo._id,
        active: true,
      });
    }
  };

  const menuItems: MenuProps["items"] = [
    {
      label: "Details",
      key: "details",
      icon: <ZoomInOutlined />,
    },
    {
      label: "Add New Contact",
      key: "new-contact",
      icon: <UserAddOutlined />,
    },
    {
      label: "Add New Note",
      key: "new-note",
      icon: <FileAddOutlined />,
    },
    {
      label: "Add New Activity",
      key: "new-activity",
      disabled: !projectSelected,
      icon: <FormOutlined />,
    },
    {
      label: "Add New Task",
      key: "new-task",
      disabled: !projectSelected,
      icon: <ClockCircleOutlined />,
    },
    {
      label: "Add New Project",
      key: "new-project",
      icon: <FolderAddOutlined />,
      children: [
        {
          label: "Create New Project",
          key: "new-project-create",
        },
        {
          label: "Select existing project",
          key: "select-from-existing",
        },
      ],
    },
  ];

  const onClickMenuItem: MenuProps["onClick"] = ({ key }) => {
    switch (key) {
      case "details":
        setCollapsed(!collapsed);
        break;
      case "new-contact":
        openItemModal("contact", true, {
          ...CONTACT_INITIAL_STATE,
          customer: customerInfo._id,
        });
        break;
      case "new-note":
        setNoteModal({ open: true, payload: NOTE_INITIAL_STATE });
        break;
      case "new-activity":
        openItemModal("activity", true, {
          ...ACTIVITY_INITIAL_STATE,
          customer: customerInfo._id,
          project: projectSelected,
          assignedTo: projectAssigned,
          origin: "customer",
        });
        break;
      case "new-task":
        openItemModal("task", true, {
          ...TASK_INITIAL_STATE,
          origin: "customer",
          customer: customerInfo._id,
          project: projectSelected,
          assignedTo: projectAssigned,
        });
        break;
      case "new-project-create":
        onCreateProject();
        break;
      case "select-from-existing":
        onCreateProject(true);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Layout>
        <Sider
          breakpoint="sm"
          trigger={null}
          collapsed={collapsed}
          collapsible
          width={siderWidth}
          style={{ padding: "0 16px" }} // Adjust height based on layout
          theme="light"
          onBreakpoint={(broken) => {
            if (broken) {
              setCollapsed(true);
              setSiderWidth(450);
            }
          }}
        >
          {collapsed ? (
            <Menu onClick={onClickMenuItem} mode="vertical" items={menuItems} />
          ) : (
            <Space
              direction="vertical"
              size={0}
              style={{
                width: xs ? "80%" : "100%",
                padding: xs ? "8px 0" : "16px 0",
                minWidth: xs ? 0 : 320,
                maxWidth: "100%",
              }}
            >
              <Space
                direction={xs ? "vertical" : "horizontal"}
                style={{
                  width: "100%",
                  flexWrap: "wrap",
                  gap: xs ? 8 : 16,
                  justifyContent: xs ? "flex-start" : "flex-start",
                  alignItems: xs ? "stretch" : "center",
                }}
              >
                <Form.Item
                  label="Assigned to"
                  style={{
                    marginBottom: xs ? 8 : 0,
                    width: xs ? "100%" : 220,
                    minWidth: 180,
                    flex: xs ? "1 1 100%" : "0 0 auto",
                  }}
                  labelCol={{ span: xs ? 24 : undefined }}
                  wrapperCol={{ span: xs ? 24 : undefined }}
                >
                  <Select
                    filterOption={filterOption}
                    showSearch
                    style={{ width: "100%" }}
                    disabled={!projectSelected}
                    value={projectAssigned || null}
                    options={userOptions}
                    mode="multiple"
                    maxTagCount={xs ? 2 : 4}
                    onChange={(value) => {
                      setProjectAssigned(value);
                      handleEditData({
                        endpoint: `customer/${idClient}/${customerInfo._id}`,
                        newData: {
                          assignedChange: true,
                          assignedTo: value,
                          id: projectSelected,
                        },
                        next: () => {
                          getCustomerData(customerInfo._id, idClient, () => {});
                        },
                      });
                    }}
                  />
                </Form.Item>
                <Tooltip title="Add new contact">
                  <Button
                    onClick={() =>
                      openItemModal("contact", true, {
                        ...CONTACT_INITIAL_STATE,
                        customer: customerInfo._id,
                      })
                    }
                    icon={<UserAddOutlined />}
                    type="default"
                    style={{ width: xs ? "100%" : undefined }}
                  />
                </Tooltip>
                <Tooltip title="Add new Note">
                  <Button
                    icon={<FileAddOutlined />}
                    onClick={() =>
                      setNoteModal({
                        open: true,
                        payload: NOTE_INITIAL_STATE,
                      })
                    }
                    style={{ width: xs ? "100%" : undefined }}
                  />
                </Tooltip>
                <Tooltip title="Add new Activity">
                  <Button
                    icon={<FormOutlined />}
                    disabled={!projectSelected}
                    onClick={() =>
                      openItemModal("activity", true, {
                        ...ACTIVITY_INITIAL_STATE,
                        customer: customerInfo._id,
                        project: projectSelected,
                        assignedTo: projectAssigned,
                        origin: "customer",
                      })
                    }
                    style={{ width: xs ? "100%" : undefined }}
                  />
                </Tooltip>
                <Tooltip title="Add new Task">
                  <Button
                    icon={<ClockCircleOutlined />}
                    disabled={!projectSelected}
                    onClick={() =>
                      openItemModal("task", true, {
                        TASK_INITIAL_STATE,
                        origin: "customer",
                        customer: customerInfo._id,
                        project: projectSelected,
                        assignedTo: projectAssigned,
                      })
                    }
                    style={{ width: xs ? "100%" : undefined }}
                  />
                </Tooltip>
              </Space>

              <Divider />
              <NoteList notes={customerInfo.CustomerNotes} />
              <Divider />
              <ContactList
                contacts={contactList}
                filterContacts={(search) => {
                  getData("contacts", idClient, search, {
                    customer: customerInfo._id,
                  });
                }}
                idClient={idClient}
                next={() =>
                  getData("contacts", idClient, undefined, {
                    customer: customerInfo._id,
                  })
                }
              />
            </Space>
          )}
        </Sider>
        <Layout>
          <Header
            className="protected-layout__header"
            style={{ padding: 0, maxHeight: "30px" }}
          >
            <Tooltip title="Details">
              <Button
                type="text"
                disabled={xs}
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
              />
            </Tooltip>

            <div style={{ marginRight: "20px" }}>
              <TagSelector
                disabled={!projectSelected}
                className="summary-client__card__actions__select"
                size="large"
                selectedTags={tagsSelected}
                setSelectedTags={(selected) => handleFilterByTags(selected)}
                filtering={true}
              />
            </div>
          </Header>

          <Content className="protected-layout__dashboard">
            <>
              <TaskList
                origin="customer"
                onAddNewTask={() =>
                  openItemModal("task", true, {
                    ...TASK_INITIAL_STATE,
                    origin: "customer",
                    customer: customerInfo._id,
                    project: projectSelected,
                    assignedTo: projectAssigned,
                  })
                }
                onEditTask={(task) =>
                  openItemModal("task", false, { ...task, origin: "customer" })
                }
                onDuplicateTask={(task) =>
                  openItemModal("task", true, {
                    ...task,
                    _id: "",
                    description: task.description + " (Copy)",
                    origin: "customer",
                  })
                }
                onChangePage={(current, pageSize) => {
                  getData("tasks", idClient, undefined, {
                    project: projectSelected,
                    customer: customerInfo._id,
                    active: true,
                    page: current,
                    perPage: pageSize,
                  });
                }}
                onDeleteTask={(task, _, pageSize, currentPage) =>
                  handleDeleteData({
                    endpoint: `task/${idClient}/${task._id}`,
                    next: () => {
                      getData("tasks", idClient, undefined, {
                        project: projectSelected,
                        customer: customerInfo._id,
                        active: true,
                        page: currentPage,
                        perPage: pageSize,
                      });
                    },
                  })
                }
              />
              <ActivityList
                origin="customer"
                onAddNewActivity={() =>
                  openItemModal("activity", true, {
                    ...ACTIVITY_INITIAL_STATE,
                    customer: customerInfo._id,
                    project: projectSelected,
                    assignedTo: projectAssigned,
                    origin: "customer",
                  })
                }
                onEditActivity={(activity) =>
                  openItemModal("activity", false, {
                    ...activity,
                    origin: "customer",
                  })
                }
                onDuplicateActivity={(activity) =>
                  openItemModal("activity", true, {
                    ...activity,
                    _id: "",
                    description: activity.description + " (Copy)",
                    origin: "customer",
                  })
                }
                onChangePage={(current, pageSize) => {
                  getData("activities-paginated", idClient, undefined, {
                    project: projectSelected,
                    customer: customerInfo._id,
                    active: true,
                    page: current,
                    perPage: pageSize,
                  });
                }}
                onDeleteActivity={(activity, pageSize, currentPage) => {
                  handleDeleteData({
                    endpoint: `activity/${idClient}/${activity._id}`,
                    next: () => {
                      getData("activities-paginated", idClient, undefined, {
                        project: projectSelected,
                        customer: customerInfo._id,
                        active: true,
                        page: currentPage,
                        perPage: pageSize,
                      });
                    },
                  });
                }}
              />
            </>
          </Content>
        </Layout>
      </Layout>

      <ModalCreateOrUpdateNote
        onCancel={() => setNoteModal({ open: false, payload: null })}
        modalStatus={noteModal}
        onSubmit={(values) => {
          if (profileInfo) {
            let newNote = {
              ...values,
              noteBy: profileInfo._id,
            };
            handleEditData({
              endpoint: `customer/${idClient}/${customerInfo._id}`,
              newData: { newNote },
              next: () => {
                getCustomerData(customerInfo._id, idClient, () => {});
                setNoteModal({ open: false, payload: null });
              },
            });
          } else {
            setNoteModal({ open: false, payload: null });
          }
        }}
      />
    </>
  );
};
